import React, { useEffect, useState, useCallback, useRef } from "react";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import { FaceLivenessDetector } from "@aws-amplify/ui-react-liveness";
import {
  Loader,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
} from "@aws-amplify/ui-react";
import {
  RekognitionClient,
  GetFaceLivenessSessionResultsCommand,
  DetectFacesCommand,
} from "@aws-sdk/client-rekognition";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { verifyProcess, trackPageVisiting, startVerification, getSessionStatus, reportFailure } from "../../services/faceLiveness";
import {
  S3Client,
  GetObjectCommand,
  DeleteObjectCommand,
} from "@aws-sdk/client-s3";
import { Typography, Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, Link } from "@mui/material";
import { images } from "../../assets";
import "./index.css";
import { decrypt } from "../../utils/functions";
import { guestCredentialProvider } from "./guestCredentials";
import AppLayout from "../../components/layout";
import useVerifiedNavigation from "../../hooks/useVerifiedNavigation";
import useSessionStatusPolling from "../../hooks/useSessionStatusPolling";
import { clearNavigationWarnings } from "../../hooks/useNavigationWarning";
import { isMobile } from "../../utils/technicalInfo";

import debugLogger from "../../utils/debug";
import { logger } from "../../store/middleware/consoleEventLogger";
import { useLanguage } from "../../contexts/LanguageContext";

const FaceVerify = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const token = JSON.parse(localStorage.getItem("token"));
  const location = useLocation();
  const age_limit = location?.state?.age_limit ?? 0;
  const keys = location?.state?.keys ?? [];
  const redirect_url = location?.state?.redirect_url ?? "";
  const [awsSessionId, setAwsSessionId] = useState(location?.state?.awsSessionId);
  const fromQR = location?.state?.fromQR ?? false;
  const initialLanguage = location?.state?.language || "en";
  const { language, setLanguage, t } = useLanguage();

  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);
  const [cameraAvailable, setCameraAvailable] = useState(null); // null = checking, true = available, false = not available
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [verificationStarted, setVerificationStarted] = useState(false);
  const [previewStream, setPreviewStream] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showCameraHelpModal, setShowCameraHelpModal] = useState(false);
  const [storedRedirectUrl, setStoredRedirectUrl] = useState(null);
  // Camera switching variables removed
  const previewVideoRef = useRef(null);
  const originalGetUserMediaRef = useRef(null);
  const hasRedirectedRef = useRef(false);
  const mobile = isMobile();

  const performRedirect = useCallback((url) => {
    if (hasRedirectedRef.current) return;
    hasRedirectedRef.current = true;
    clearNavigationWarnings(() => {
      window.location.href = url;
    });
  }, []);

  // Sync language from location state if provided
  useEffect(() => {
    if (initialLanguage && initialLanguage !== language) {
      setLanguage(initialLanguage);
    }
  }, [initialLanguage, language, setLanguage]);

  // Initialize stored redirect URL from location state
  useEffect(() => {
    if (redirect_url && !storedRedirectUrl) {
      setStoredRedirectUrl(redirect_url);
    }
  }, [redirect_url, storedRedirectUrl]);

  // Fetch session data if not provided through state
  useEffect(() => {
    if (!awsSessionId && sessionId) {
      debugLogger.log('AWS Session ID not in state, fetching from API', { sessionId });

      const fetchSessionData = async () => {
        try {
          const response = await startVerification(sessionId);
          debugLogger.log('startVerification response received', {
            status: response?.status,
            hasAwsSessionId: !!response?.data?.aws_session_id,
            hasRedirectUrl: !!response?.data?.redirect_url
          });

          if (response?.status === 200 || response?.status === 201) {
            const sessionKey = response?.data?.aws_session_id;
            const redirectUrl = response?.data?.redirect_url;

            // Store the redirect URL for use in error handling
            if (redirectUrl) {
              setStoredRedirectUrl(redirectUrl);
            }

            if (sessionKey) {
              setAwsSessionId(sessionKey);
              debugLogger.log('Session data fetched successfully', {
                awsSessionId: '***' + sessionKey.slice(-8),
                redirectUrl: redirectUrl
              });
            } else {
              debugLogger.log('No AWS Session ID in response, will redirect to failed process', {
                redirectUrl: redirectUrl
              });

              if (redirectUrl) {
                performRedirect(redirectUrl);
              }
            }
          }
        } catch (err) {
          debugLogger.error('startVerification', err, { sessionId });
          setError(err);
        }
      };

      fetchSessionData();
    }
  }, [sessionId, awsSessionId]);

  // Poll for session status to handle cancellation from other device
  useSessionStatusPolling(sessionId, mobile && fromQR, language, storedRedirectUrl);

  // AWS configuration constants
  const REGION = process.env.REACT_APP_REGION;
  const IDENTITY_POOL_ID = keys.identityId;

  // Initialize S3 client inside a function to avoid hoisting issues
  const getS3Client = useCallback(() => {
    const s3Client = new S3Client({
      region: REGION,
      credentials: guestCredentialProvider(),
    });

    debugLogger.log('S3 Client initialized', {
      region: REGION,
      credentialsProvider: 'guestCredentialProvider'
    });

    return s3Client;
  }, [REGION]);

  // Browser detection function
  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    let browserName = "Unknown";
    let browserVersion = "Unknown";

    if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edg") === -1) {
      browserName = "Chrome";
      browserVersion = userAgent.match(/Chrome\/([0-9.]+)/)?.[1] || "Unknown";
    } else if (userAgent.indexOf("Firefox") > -1) {
      browserName = "Firefox";
      browserVersion = userAgent.match(/Firefox\/([0-9.]+)/)?.[1] || "Unknown";
    } else if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
      browserName = "Safari";
      browserVersion = userAgent.match(/Version\/([0-9.]+)/)?.[1] || "Unknown";
    } else if (userAgent.indexOf("Edg") > -1) {
      browserName = "Edge";
      browserVersion = userAgent.match(/Edg\/([0-9.]+)/)?.[1] || "Unknown";
    }

    return { browserName, browserVersion };
  };

  // Get camera permission instructions based on browser
  const getCameraInstructions = () => {
    const { browserName } = getBrowserInfo();

    switch (browserName) {
      case "Chrome":
        return {
          title: "Enable Camera in Chrome",
          steps: [
            "1. Click the camera icon in the address bar (URL bar)",
            "2. Select 'Always allow' for camera access",
            "3. Click 'Done' and refresh the page",
            "",
            "Alternative method:",
            "1. Click the three dots menu (⋮) in the top right",
            "2. Go to Settings > Privacy and security > Site Settings",
            "3. Click on 'Camera'",
            "4. Make sure this site is allowed to use your camera"
          ]
        };
      case "Firefox":
        return {
          title: "Enable Camera in Firefox",
          steps: [
            "1. Click the camera icon in the address bar",
            "2. Select 'Allow' for camera access",
            "3. Refresh the page",
            "",
            "Alternative method:",
            "1. Click the menu button (☰) in the top right",
            "2. Go to Settings > Privacy & Security",
            "3. Scroll down to 'Permissions' section",
            "4. Click 'Settings' next to 'Camera'",
            "5. Find this website and change status to 'Allow'"
          ]
        };
      case "Safari":
        return {
          title: "Enable Camera in Safari",
          steps: [
            "1. Go to Safari menu > Preferences",
            "2. Click on the 'Websites' tab",
            "3. Select 'Camera' from the left sidebar",
            "4. Find this website and set it to 'Allow'",
            "5. Refresh the page",
            "",
            "On macOS Monterey or later:",
            "1. Click the camera icon in the address bar",
            "2. Select 'Allow' for camera access"
          ]
        };
      case "Edge":
        return {
          title: "Enable Camera in Microsoft Edge",
          steps: [
            "1. Click the camera icon in the address bar",
            "2. Select 'Allow' for camera access",
            "3. Refresh the page",
            "",
            "Alternative method:",
            "1. Click the three dots menu (...) in the top right",
            "2. Go to Settings > Site permissions",
            "3. Click on 'Camera'",
            "4. Make sure this site is allowed to use your camera"
          ]
        };
      default:
        return {
          title: "Enable Camera Access",
          steps: [
            "1. Look for a camera icon in your browser's address bar",
            "2. Click on it and select 'Allow' for camera access",
            "3. Refresh the page",
            "",
            "If you don't see a camera icon:",
            "1. Go to your browser's settings",
            "2. Look for 'Privacy', 'Security', or 'Site Settings'",
            "3. Find 'Camera' permissions",
            "4. Make sure this website is allowed to use your camera"
          ]
        };
    }
  };

  // Debug component mount
  useEffect(() => {
    debugLogger.componentMount('FaceVerify', {
      sessionId,
      awsSessionId,
      age_limit,
      redirect_url,
      initialLanguage,
      keys: keys ? 'Present' : 'Missing'
    });
    debugLogger.browserInfo();
    debugLogger.sessionInfo(sessionId, awsSessionId, keys);
    debugLogger.localStorage('get', 'token', token ? 'Present' : 'Missing');

    return () => {
      debugLogger.componentUnmount('FaceVerify');
    };
  }, []);

  // Debug state changes
  useEffect(() => {
    debugLogger.stateChange('FaceVerify', 'status', '', status);
  }, [status]);

  useEffect(() => {
    debugLogger.stateChange('FaceVerify', 'language', initialLanguage, language);
  }, [language]);

  useEffect(() => {
    debugLogger.stateChange('FaceVerify', 'error', null, error?.message);

    if (error && error !== null && !hasRedirectedRef.current) {
      debugLogger.log('Error detected', {
        errorName: error?.name,
        errorMessage: error?.message,
        redirectUrl: redirect_url,
        hasRedirectUrl: !!redirect_url
      });
      hasRedirectedRef.current = true;

      if (error?.message?.includes("No camera found")) {
        debugLogger.log('No camera found, navigating to /no-camera route');
        navigate(`/no-camera/${sessionId}`, { state: { redirect_url: storedRedirectUrl } });
      } else if (storedRedirectUrl) {
        // Redirect to the provided redirect_url (e.g., failed process page)
        debugLogger.log('Redirecting to provided redirect_url', {
          redirectUrl: storedRedirectUrl
        });
        performRedirect(storedRedirectUrl);
      } else {
        // Redirect to /failed route if no redirect_url is available
        debugLogger.log('No redirect_url provided, navigating to /failed route');
        navigate('/failed', { state: { redirect_url: storedRedirectUrl } });
      }
    }
  }, [error, storedRedirectUrl, navigate]);

  // Camera detection function
  const checkCameraAvailability = useCallback(async () => {
    debugLogger.cameraAccess('request', { action: 'checking availability' });

    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        debugLogger.cameraAccess('error', { reason: 'MediaDevices API not supported' });
        logger.error('Camera detection failed: MediaDevices API not supported', {
          userAgent: navigator.userAgent,
          platform: navigator.platform
        });
        reportFailure(sessionId, 'mediaDevices_not_supported');
        setCameraAvailable(false);
        setError(new Error("No camera found. We cannot proceed with verification."));
        return;
      }

      // Check if any video input devices are available
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');

      debugLogger.cameraAccess('granted', {
        totalDevices: devices.length,
        videoDevices: videoDevices.length,
        devices: videoDevices.map(d => ({ deviceId: d.deviceId, label: d.label }))
      });

      if (videoDevices.length === 0) {
        debugLogger.cameraAccess('error', { reason: 'No video input devices found' });
        logger.error('Camera detection failed: No video input devices found', {
          totalDevices: devices.length,
          deviceTypes: devices.map(d => d.kind)
        });
        reportFailure(sessionId, 'camera_not_detected');
        setCameraAvailable(false);
        setAvailableCameras([]);
        setError(new Error("No camera found. We cannot proceed with verification."));
        return;
      }

      // Store available cameras with user-friendly labels
      const cameraList = videoDevices.map((device, index) => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
        groupId: device.groupId
      }));

      setAvailableCameras(cameraList);

      // Always select front-facing camera for age verification
      const frontCamera = cameraList.find(camera =>
        camera.label.toLowerCase().includes('front') ||
        camera.label.toLowerCase().includes('user') ||
        camera.label.toLowerCase().includes('facing')
      ) || cameraList[0];

      const finalCamera = frontCamera;
      // Clear any stored camera selections
      localStorage.removeItem('selectedCameraId');
      localStorage.removeItem('selectedCameraIndex');

      setSelectedCameraId(finalCamera.deviceId);

      debugLogger.log('Front-facing camera selected automatically', {
        count: cameraList.length,
        cameras: cameraList,
        selectedCamera: finalCamera.deviceId
      });

      // Try to access the final selected camera to ensure it's actually available
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: finalCamera.deviceId ? { exact: finalCamera.deviceId } : undefined,
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        });

        // Stop the stream immediately as we just wanted to test access
        stream.getTracks().forEach(track => track.stop());

        debugLogger.cameraAccess('granted', {
          reason: 'Camera access successful',
          streamActive: true,
          selectedCamera: finalCamera.deviceId
        });
        setCameraAvailable(true);

        // Set up camera override for the selected camera
        if (finalCamera.deviceId && cameraList.length > 1) {
          // Store original function
          if (!originalGetUserMediaRef.current) {
            originalGetUserMediaRef.current = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
          }

          // Override getUserMedia globally
          navigator.mediaDevices.getUserMedia = function(constraints) {
            debugLogger.log('getUserMedia called with constraints', constraints);

            if (constraints && constraints.video && typeof constraints.video === 'object') {
              const modifiedConstraints = {
                ...constraints,
                video: {
                  ...constraints.video,
                  deviceId: { exact: finalCamera.deviceId }
                }
              };
              debugLogger.log('Modified constraints for selected camera', {
                original: constraints,
                modified: modifiedConstraints,
                selectedCameraId: finalCamera.deviceId
              });
              return originalGetUserMediaRef.current(modifiedConstraints);
            }
            return originalGetUserMediaRef.current(constraints);
          };

          debugLogger.log('Camera override installed for selected camera', { selectedCameraId: finalCamera.deviceId });
        }

        // Always start verification automatically with selected camera
        setVerificationStarted(true);
        debugLogger.log('Camera detected, starting verification automatically with selected camera', {
          selectedCamera: finalCamera.deviceId,
          totalCameras: cameraList.length
        });

      } catch (streamError) {
        debugLogger.cameraAccess('denied', {
          error: streamError.name,
          message: streamError.message
        });
        logger.error(`Camera access denied/failed: ${streamError.name}`, {
          message: streamError.message,
          name: streamError.name
        });

        if (streamError.name === 'NotAllowedError') {
          setError(new Error("Camera permission denied. Please allow camera access and refresh the page."));
        } else if (streamError.name === 'NotFoundError') {
          setError(new Error("No camera found. We cannot proceed with verification."));
        } else {
          setError(new Error("Camera is not accessible. Please check your camera settings and try again."));
        }
        setCameraAvailable(false);
      }

    } catch (error) {
      debugLogger.cameraAccess('error', {
        error: error.name,
        message: error.message
      });
      logger.error('Unexpected error during camera availability check', {
        error: error.name,
        message: error.message,
        stack: error.stack
      });
      setCameraAvailable(false);
      setError(new Error("Unable to check camera availability. Please ensure your browser supports camera access."));
    }
  }, []);

  // Start camera preview
  const startCameraPreview = useCallback(async (cameraId) => {
    // Prevent multiple simultaneous calls
    if (previewLoading) {
      debugLogger.log('Preview already loading, skipping');
      return;
    }

    try {
      setPreviewLoading(true);

      // Stop existing stream if any
      if (previewStream) {
        previewStream.getTracks().forEach(track => track.stop());
        setPreviewStream(null);
      }

      debugLogger.cameraAccess('request', { action: 'starting preview', cameraId });

      let stream;

      // Try with exact device ID first
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: cameraId ? { exact: cameraId } : undefined,
            width: { ideal: 320 },
            height: { ideal: 240 }
          }
        });
      } catch (exactError) {
        debugLogger.log('Exact device ID failed, trying with ideal', { error: exactError.name });

        // Fallback: try with ideal device ID
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: cameraId ? { ideal: cameraId } : undefined,
              width: { ideal: 320 },
              height: { ideal: 240 }
            }
          });
        } catch (idealError) {
          debugLogger.log('Ideal device ID failed, trying basic constraints', { error: idealError.name });

          // Final fallback: basic video constraints
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              width: { ideal: 320 },
              height: { ideal: 240 }
            }
          });
        }
      }

      debugLogger.log('Stream obtained', {
        streamId: stream.id,
        tracks: stream.getVideoTracks().length,
        trackSettings: stream.getVideoTracks()[0]?.getSettings()
      });

      // Set stream first
      setPreviewStream(stream);

      // Then assign to video element
      if (previewVideoRef.current && stream.active) {
        previewVideoRef.current.srcObject = stream;

        // Ensure video plays
        previewVideoRef.current.onloadedmetadata = () => {
          debugLogger.log('Video metadata loaded, attempting to play');
          setPreviewLoading(false);
          previewVideoRef.current.play().catch(e => {
            debugLogger.error('Video play failed', e);
            setPreviewLoading(false);
          });
        };

        // Handle video errors
        previewVideoRef.current.onerror = (e) => {
          debugLogger.error('Video element error', e);
          setPreviewLoading(false);
        };
      } else {
        setPreviewLoading(false);
      }

      debugLogger.cameraAccess('granted', {
        action: 'preview started',
        cameraId,
        streamActive: stream.active,
        videoTracks: stream.getVideoTracks().length
      });

    } catch (error) {
      setPreviewLoading(false);
      debugLogger.cameraAccess('error', {
        action: 'preview failed',
        cameraId,
        error: error.name,
        message: error.message
      });
      logger.error('Failed to start camera preview:', error);
    }
  }, [previewStream, previewLoading]);

  // Stop camera preview
  const stopCameraPreview = useCallback(() => {
    if (previewStream) {
      previewStream.getTracks().forEach(track => track.stop());
      setPreviewStream(null);
      debugLogger.cameraAccess('granted', { action: 'preview stopped' });
    }
  }, [previewStream]);

  // Camera selection and switching functionality removed

  // Check camera availability when component mounts and awsSessionId is available
  useEffect(() => {
    if (awsSessionId && cameraAvailable === null) {
      checkCameraAvailability();
    }
  }, [awsSessionId, cameraAvailable, checkCameraAvailability]);

  // No camera preview needed since we're automatically selecting the front-facing camera

  // Cleanup preview stream when component unmounts or verification starts
  useEffect(() => {
    return () => {
      stopCameraPreview();

      // Restore original getUserMedia if it was overridden
      if (originalGetUserMediaRef.current) {
        navigator.mediaDevices.getUserMedia = originalGetUserMediaRef.current;
        debugLogger.log('Restored original getUserMedia on cleanup');
        originalGetUserMediaRef.current = null;
      }
    };
  }, [stopCameraPreview]);

  // Unmount-only safety net for the global getUserMedia patch.
  //
  // The cleanup above is keyed on stopCameraPreview, so it re-registers whenever
  // that callback's identity changes. This effect has no dependencies, so it can
  // only ever run on unmount, guaranteeing the patched getUserMedia is restored
  // before another route mounts. Document capture needs the rear camera and the
  // patch forces the front one, so a leak across routes would break it.
  useEffect(() => {
    return () => {
      if (originalGetUserMediaRef.current) {
        navigator.mediaDevices.getUserMedia = originalGetUserMediaRef.current;
        originalGetUserMediaRef.current = null;
        debugLogger.log('Restored original getUserMedia on unmount');
      }
    };
  }, []);

  // Stop preview when verification starts
  useEffect(() => {
    if (verificationStarted) {
      stopCameraPreview();
    }
  }, [verificationStarted, stopCameraPreview]);

  // Debug AWS configuration
  debugLogger.log('AWS Configuration', {
    REGION,
    IDENTITY_POOL_ID: IDENTITY_POOL_ID ? '***' + IDENTITY_POOL_ID.slice(-8) : 'Missing',
    REACT_APP_REGION: process.env.REACT_APP_REGION
  });



  const dictionary = {
    en: null, // English default
    es: {
      photosensitivyWarningHeadingText: "Advertencia de fotosensibilidad",
      photosensitivyWarningBodyText:
        "Esta verificación muestra luces de colores. Tenga cuidado si es fotosensible.",
      goodFitCaptionText: "Buen ajuste",
      tooFarCaptionText: "Demasiado lejos",
      hintCenterFaceText: "Centra tu cara",
      startScreenBeginCheckText: "Comenzar a verificar",
      hintTooFarText: "Acércate",
      hintHoldFaceForFreshnessText: "Quédate quieto",
    },
    it: {
      photosensitivyWarningHeadingText: "Avviso di fotosensibilità",
      photosensitivyWarningBodyText:
        "Questa verifica mostra luci colorate. Prestare attenzione se si è fotosensibili.",
      goodFitCaptionText: "Buona inquadratura",
      tooFarCaptionText: "Troppo lontano",
      hintCenterFaceText: "Centra il viso",
      startScreenBeginCheckText: "Inizia la verifica",
      hintTooFarText: "Avvicinarsi",
      hintHoldFaceForFreshnessText: "Stai fermo",
    },
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const warningEl = document.querySelector(
        ".amplify-liveness-start-screen-warning"
      );
      if (warningEl) warningEl.remove();

      const cameraEl = document.querySelector(
        ".amplify-liveness-recording-icon-container"
      );
      if (cameraEl) cameraEl.style.display = "none";

      const cameraModule = document.querySelector(
        ".amplify-liveness-camera-module"
      );
      if (cameraModule) cameraModule.style.border = "none";

      const button = document.querySelector(
        ".amplify-button.amplify-field-group__control.amplify-button--primary"
      );
      if (button) {
        button.style.backgroundColor = "#FD0000FF";
        button.style.color = "#ffffff";
        button.style.borderColor = "#FD0000FF";
        button.textContent = "Start Verification";

        if (awsSessionId) {
          button.click();
        }
      }

      // Clear interval if all elements are found and clicked if needed
      if (cameraModule && button && (!awsSessionId || button)) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [awsSessionId]);

  // Optimize MutationObserver for recording icon removal
  useEffect(() => {
    const removeRecordingIcon = () => {
      const traverseShadowRoots = (node) => {
        if (!node) return false;
        if (node.shadowRoot) {
          const target = node.shadowRoot.querySelector(
            ".amplify-liveness-recording-icon-container"
          );
          if (target) {
            target.style.display = "none";
            return true;
          }
          return Array.from(node.shadowRoot.querySelectorAll("*")).some(
            traverseShadowRoots
          );
        }
        return Array.from(node.children).some(traverseShadowRoots);
      };
      traverseShadowRoots(document.body);
    };

    const observer = new MutationObserver(removeRecordingIcon);
    observer.observe(document.body, { childList: true, subtree: true });
    removeRecordingIcon();

    return () => observer.disconnect();
  }, []);

  // Initiate verification and track page visit
  useEffect(() => {
    if (sessionId) {
      handleTrackPageVisiting();
    }
  }, [sessionId]);

  const handleTrackPageVisiting = useCallback(async () => {
    const startTime = Date.now();
    debugLogger.apiRequest('POST', `trackPageVisiting/${sessionId}/track`);

    try {
      const response = await trackPageVisiting(sessionId);
      debugLogger.apiResponse('POST', `trackPageVisiting/${sessionId}/track`, response?.status || 200, response?.data);
      debugLogger.performance('trackPageVisiting', startTime);
      logger.log("trackPageVisiting API response:", response);
    } catch (error) {
      debugLogger.apiResponse('POST', `trackPageVisiting/${sessionId}/track`, error?.response?.status || 500, null, error);
      debugLogger.error('handleTrackPageVisiting', error);
      logger.error("Failed to track page visiting:", error);
    }
  }, [sessionId]);

  const handleNavigate = useCallback((url, state) => {
    debugLogger.navigation('FaceVerify', url, state);
    if (mobile && fromQR) {
      const isError = url.includes('error');
      navigate("/mobile-completed", { state: { ...state, isError, language } });
    } else {
      navigate(url, { state });
    }
  }, [mobile, fromQR, navigate, language]);

  const handleVerifiedNavigation = useVerifiedNavigation(handleNavigate);

  const livenessConfig = React.useMemo(() => ({
    faceMovementAndLightChallenge: {
      challengeConfig: {
        ovalParameters: {
          centerX: 0.5,
          centerY: 0.5,
          width: 0.8,
          height: 0.8
        },
        // Use selected front camera
        ...(selectedCameraId && {
          cameraConstraints: {
            video: {
              deviceId: { exact: selectedCameraId },
              width: { ideal: 640 },
              height: { ideal: 480 }
            }
          }
        })
      }
    },
    // Also set at root level
    ...(selectedCameraId && {
      videoConstraints: {
        deviceId: { exact: selectedCameraId },
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    })
  }), [selectedCameraId]);

  const handleAnalysisComplete = useCallback(
    async (result) => {
      const startTime = Date.now();
      let status = "error";

      debugLogger.faceLivenessEvent('analysis-complete', { result });

      try {
        // Debug AWS Rekognition setup
        debugLogger.log('Setting up AWS Rekognition client', {
          region: REGION,
          awsSessionId
        });

        const rekognition = new RekognitionClient({
          region: REGION,
          credentials: guestCredentialProvider(),
        });

        debugLogger.awsCredentials({ region: REGION });
        logger.log(rekognition, "rekognitionrekognitionrekognition")

        const command = new GetFaceLivenessSessionResultsCommand({
          SessionId: awsSessionId,
        });

        debugLogger.log('Sending GetFaceLivenessSessionResults command', { awsSessionId });
        const livenessResult = await rekognition.send(command);

        debugLogger.log('Liveness result received', {
          confidence: livenessResult.Confidence,
          status: livenessResult.Status,
          auditImages: livenessResult.AuditImages?.length || 0,
          referenceImage: !!livenessResult.ReferenceImage
        });
        debugLogger.log('Liveness check result', { confidence: livenessResult.Confidence });

        const bucket = livenessResult.ReferenceImage?.S3Object?.Bucket || null;
        const key = livenessResult.ReferenceImage?.S3Object?.Name || null;

        debugLogger.log('S3 Reference Image details', { bucket, key });

        const payload = {
          bucket,
          key,
          awsSessionId,
        };

        debugLogger.apiRequest('POST', `verifyProcess/${sessionId}`, payload);
        const response = await verifyProcess(sessionId, payload);
        debugLogger.apiResponse('POST', `verifyProcess/${sessionId}`, response?.status || 200, response?.data);

        const redirectUrl = response?.data?.redirect_url;
        const resultStatus = response?.data?.status;
        const success_page_display = response?.data?.success_page_display;

        debugLogger.log('Verification process result', { resultStatus, redirectUrl });

        if (resultStatus === "success") {
          handleVerifiedNavigation({ language, url: redirectUrl }, success_page_display, mobile && fromQR);
        } else {
          handleNavigate("/error", {
            redirect_url: redirectUrl || redirect_url,
            confidence: livenessResult.Confidence,
            reason: 'unverified'
          });
        }

        debugLogger.performance('handleAnalysisComplete', startTime);
      } catch (error) {
        debugLogger.error('handleAnalysisComplete', error, {
          sessionId,
          awsSessionId,
          region: REGION
        });
        logger.error("Error in handleAnalysisComplete:", error);

        if (error.name === "NotAuthorizedException") {
          debugLogger.log('Authentication error detected', { errorName: error.name });
          setError(
            new Error(
              "Unauthenticated access is not supported. Please sign in."
            )
          );
        } else if (error.message?.toLowerCase().includes("timeout")) {
          debugLogger.log('Timeout error detected', { errorMessage: error.message });
          status = "timeout";
        }
        setStatus(status);
        debugLogger.stateChange('FaceVerify', 'status', '', status);
      }
    },
    [awsSessionId, handleNavigate, sessionId, language, REGION]
  );

  useEffect(() => {
    debugLogger.log('Setting up DOM mutation observer for try again button');

    const observer = new MutationObserver(() => {
      const tryAgainBtn = [...document.querySelectorAll("button")].find((btn) =>
        btn.textContent?.toLowerCase().includes("try again")
      );

      if (tryAgainBtn) {
        debugLogger.domEvent('try-again-button-found', tryAgainBtn, {
          textContent: tryAgainBtn.textContent,
          redirectUrl: redirect_url
        });
        tryAgainBtn.style.display = "none";
        handleNavigate("/error", {
          reason: 'Try again button clicked'
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      debugLogger.log('Disconnecting DOM mutation observer');
      observer.disconnect();
    };
  }, [handleNavigate, redirect_url]);

  return (
    <>
      <AppLayout>
        <Box sx={{ backgroundColor: "#f5f5f7", minHeight: "100vh" }}>
          <ThemeProvider>
            <div>
            {!awsSessionId ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                  flexDirection: "column",
                }}
              >
                <img src={images.spinner} alt="loading" />
                <Typography color="primary.text">Waiting for camera authorization...</Typography>
              </div>
            ) : cameraAvailable === null ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                  flexDirection: "column",
                }}
              >
                <img src={images.spinner} alt="loading" />
                <Typography color="primary.text">Checking camera availability...</Typography>
              </div>
            ) : cameraAvailable === false ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                  flexDirection: "column",
                  padding: "20px",
                }}
              >
                <Typography
                  color="error"
                  variant="h6"
                  style={{
                    textAlign: "center",
                    marginBottom: "20px",
                    fontWeight: "bold"
                  }}
                >
                  Camera Not Available
                </Typography>
                <Typography
                  color="primary.text"
                  style={{
                    textAlign: "center",
                    marginBottom: "20px"
                  }}
                >
                  {error?.message || "No camera found. We cannot proceed with verification."}
                </Typography>
                {error?.message?.includes("Camera permission denied") && (
                  <Typography
                    color="primary.text"
                    style={{
                      textAlign: "center",
                      marginBottom: "20px"
                    }}
                  >
                    Need help? {" "}
                    <Link
                      component="button"
                      variant="body1"
                      onClick={() => setShowCameraHelpModal(true)}
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline"
                      }}
                    >
                      Check how
                    </Link>
                    {" "} to enable camera access in your browser.
                  </Typography>
                )}
                <Typography
                  color="primary.text"
                  variant="body2"
                  style={{
                    textAlign: "center",
                    opacity: 0.7,
                    marginBottom: "20px"
                  }}
                >
                  Please ensure your device has a camera and try again.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setError(null);
                    setCameraAvailable(null);
                    // This will trigger the useEffect that calls checkCameraAvailability
                  }}
                  sx={{ mt: 2 }}
                >
                  Retry Camera Access
                </Button>
              </div>
            ) : (
              <div
              // style={{
              //   height: "100%",
              // }}
              >
                {/* Camera switch button removed */}

                <div className="oval-container">
                  <FaceLivenessDetector
                    sessionId={awsSessionId}
                    region={REGION}
                    onAnalysisComplete={handleAnalysisComplete}
                    onError={(err) => {
                      debugLogger.faceLivenessEvent('error', {
                        error: err,
                        errorName: err?.name,
                        errorMessage: err?.message
                      });
                      debugLogger.cameraAccess('error', {
                        error: err?.name,
                        message: err?.message
                      });
                      
                      reportFailure(sessionId, {
                        reason: err?.name,
                        message: err?.message,
                        details: {
                          code: err?.code,
                        }
                      });
                      
                      setCameraAvailable(false);
                      // Normalize the error to ensure it has a message property
                      const normalizedError = err instanceof Error ? err : new Error(err?.message || "Camera or Liveness error occurred");
                      setError(normalizedError);
                    }}
                    onUserCancel={() => {
                      debugLogger.faceLivenessEvent('user-cancel', { redirectUrl: redirect_url });
                      if (redirect_url) {
                        performRedirect(redirect_url);
                      } else {
                        navigate(-1);
                      }
                    }}
                    onTryAgain={() => {
                      debugLogger.faceLivenessEvent('try-again', {});
                    }}
                    displayText={dictionary[language]}
                    mandatorySignIn={true}
                    disableDefaultUI={false}
                    components={{
                      PhotosensitiveWarning: () => null, // Hide warning if needed
                    }}
                    config={livenessConfig}
                  />
                </div>
              </div>
            )}


            {error && (
              <div style={{ color: "red", textAlign: "center" }}>
                Error: {error.message || "An error occurred"}
              </div>
            )}
          </div>
        </ThemeProvider>
      </Box>

      {/* Camera Help Modal */}
        <Dialog
          open={showCameraHelpModal}
          onClose={() => setShowCameraHelpModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {getCameraInstructions().title}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              {getCameraInstructions().steps.map((step, index) => (
                <Typography
                  key={index}
                  variant="body1"
                  sx={{
                    mb: step === "" ? 1 : 0.5,
                    fontWeight: step.startsWith("Alternative method:") || step.startsWith("On macOS") ? "bold" : "normal",
                    color: step === "" ? "transparent" : "inherit"
                  }}
                >
                  {step || "\u00A0"}
                </Typography>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCameraHelpModal(false)} color="primary">
              Close
            </Button>
            <Button
              onClick={() => {
                setShowCameraHelpModal(false);
                window.location.reload();
              }}
              color="primary"
              variant="contained"
            >
              Refresh Page
            </Button>
          </DialogActions>
        </Dialog>
      </AppLayout>
    </>
  );
};

export default FaceVerify;
